import isEqual from 'lodash/isEqual';
import Luminary from '../index';
import type { Slide, TextAlignment, TemplateBundle } from '../index';
import { Config } from '../video-data/config';
import {
    getTemplateValueAttribute,
    isTemplateReference,
    getTemplateIndex,
} from '../core/attributes/template/common';
import type { UserOverrides, VirtualSceneDesign } from './core';

const _getVirtualSceneDesignForScene = (
    scene: Slide,
    userOverrides: UserOverrides | null,
    ignoreUserOverrides = false,
): VirtualSceneDesign => {
    const virtualSceneDesign: VirtualSceneDesign = {
        baseDesign: scene.sceneDesign,
        modifications: {},
        sceneIds: [scene.id],
    };
    const assets_to_skip = ['watermark_1', 'attribution_1'];
    const overrides = userOverrides ? userOverrides[scene.id] : undefined;
    // iterate through all assets in the scene and add them to the virtualSceneDesign
    scene.getAssets({ deep: true }).forEach((asset) => {
        if (assets_to_skip.includes(asset.name)) {
            return;
        }
        if (Config.isStickerAsset(asset)) {
            return;
        }
        // check if we've already added to the modifications list - if so, skip!
        if (virtualSceneDesign.modifications[asset.name]) {
            return;
        }
        // add properties depending on the asset type
        if (Config.isTextAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].textSize = asset.textSize;
            virtualSceneDesign.modifications[asset.name].fontUrl = asset.normalStyle.fontUrl;
            virtualSceneDesign.modifications[asset.name].highlightFontUrl =
                asset.highlightStyle.fontUrl;
            if (ignoreUserOverrides || (overrides && overrides.text_alignment)) {
                virtualSceneDesign.modifications[asset.name].textAlign =
                    asset.textAlign as TextAlignment;
            }
            virtualSceneDesign.modifications[asset.name].normalFillColor = asset.normalStyle.fill;
            virtualSceneDesign.modifications[asset.name].highlightFillColor =
                asset.highlightStyle.fill;
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            if (scene.getActiveTextPreset() === null) {
                // the user has changed something about the text asset bounds
                virtualSceneDesign.modifications[asset.name].transformation = {
                    anchor: {
                        x: asset.transformation.anchor.x,
                        y: asset.transformation.anchor.y,
                    },
                    position: {
                        x: asset.transformation.position.x,
                        y: asset.transformation.position.y,
                    },
                };
            }
            virtualSceneDesign.modifications[asset.name].maxHeight = asset.maxHeight;
            virtualSceneDesign.modifications[asset.name].maxWidth = asset.maxWidth;
            virtualSceneDesign.modifications[asset.name].hasUserChangedMaxWidth =
                asset.hasUserChangedMaxWidth;
        } else if (Config.isFancyCaptionAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].textSize = asset.textSize;
            virtualSceneDesign.modifications[asset.name].fontUrl = asset.normalStyle.fontUrl;
            if (ignoreUserOverrides || (overrides && overrides.text_alignment)) {
                virtualSceneDesign.modifications[asset.name].textAlign = asset.textAlign;
            }
            virtualSceneDesign.modifications[asset.name].normalFillColor = asset.normalStyle.fill;
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            if (scene.getActiveTextPreset() === null) {
                // the user has changed something about the text asset bounds
                virtualSceneDesign.modifications[asset.name].transformation = {
                    anchor: {
                        x: asset.transformation.anchor.x,
                        y: asset.transformation.anchor.y,
                    },
                    position: {
                        x: asset.transformation.position.x,
                        y: asset.transformation.position.y,
                    },
                };
            }
            virtualSceneDesign.modifications[asset.name].maxHeight = asset.maxHeight;
            virtualSceneDesign.modifications[asset.name].maxWidth = asset.maxWidth;
            virtualSceneDesign.modifications[asset.name].hasUserChangedMaxWidth =
                asset.hasUserChangedMaxWidth;
        } else if (Config.isAssetGroup(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            if (ignoreUserOverrides || (overrides && overrides.text_alignment)) {
                virtualSceneDesign.modifications[asset.name].alignChildren = asset.alignChildren;
            }
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            if (scene.getActiveTextPreset() === null) {
                // the user has changed something about the asset group bounds
                virtualSceneDesign.modifications[asset.name].transformation = {
                    anchor: {
                        x: asset.transformation.anchor.x,
                        y: asset.transformation.anchor.y,
                    },
                    position: {
                        x: asset.transformation.position.x,
                        y: asset.transformation.position.y,
                    },
                };
                virtualSceneDesign.modifications[asset.name].maxHeight = asset.maxHeight;
                virtualSceneDesign.modifications[asset.name].hasUserChangedMaxWidth =
                    asset.hasUserChangedMaxWidth;
            }
        } else if (Config.isMediaAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            if (ignoreUserOverrides || (overrides && overrides.brightness)) {
                virtualSceneDesign.modifications[asset.name].brightness = asset.brightness;
            }
            if (ignoreUserOverrides || (overrides && overrides.background_animation)) {
                virtualSceneDesign.modifications[asset.name].backgroundAnimation = asset.kenburn;
            }
            virtualSceneDesign.modifications[asset.name].contrast = asset.contrast;
            virtualSceneDesign.modifications[asset.name].hue = asset.hue;
            virtualSceneDesign.modifications[asset.name].saturation = asset.saturation;
            virtualSceneDesign.modifications[asset.name].lightness = asset.lightness;
            virtualSceneDesign.modifications[asset.name].blur = asset.blur;
            virtualSceneDesign.modifications[asset.name].vignetting = asset.vignetting;
            virtualSceneDesign.modifications[asset.name].noise = asset.noise;
            virtualSceneDesign.modifications[asset.name].grayscale = asset.enableGreyscale;
            if (asset.isPlaybackRateOverridden && asset.playbackRate !== 1) {
                virtualSceneDesign.modifications[asset.name].playbackRate = asset.playbackRate;
            }
        } else if (Config.isIconAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            virtualSceneDesign.modifications[asset.name].colorMaps = asset.colorMaps.map(
                (colorMap) => colorMap.targetColor,
            );
        } else if (Config.isLottieAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            virtualSceneDesign.modifications[asset.name].colorMaps = asset.colorMaps.map(
                (colorMap) => colorMap.targetColor,
            );
        } else if (Config.isCircleAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            virtualSceneDesign.modifications[asset.name].color = asset.color;
            virtualSceneDesign.modifications[asset.name].lineColor = asset.lineColor;
        } else if (Config.isRectangleAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            virtualSceneDesign.modifications[asset.name].color = asset.color;
            virtualSceneDesign.modifications[asset.name].lineColor = asset.lineColor;
        } else if (Config.isGradientAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].colorGraphicsMap =
                asset.buildColorGraphicsMap();
            virtualSceneDesign.modifications[asset.name].colorStops = asset.colorStops.map(
                (colorStop) => colorStop.color,
            );
        } else if (Config.isColorAsset(asset)) {
            virtualSceneDesign.modifications[asset.name] = {};
            virtualSceneDesign.modifications[asset.name].sourceColor = asset.sourceColor;
        }
    });

    // set scene-level properties
    virtualSceneDesign.modifications[Config.SCENE_MODIFICATION_KEY] = {};
    if (ignoreUserOverrides || (overrides && overrides.text_position)) {
        const activeTextPreset = scene.getActiveTextPreset();
        if (activeTextPreset) {
            virtualSceneDesign.modifications[Config.SCENE_MODIFICATION_KEY].textPositionPreset =
                activeTextPreset;
        }
    }
    const watermarkPosition = scene.watermark ? scene.watermark.position : undefined;
    if (watermarkPosition) {
        virtualSceneDesign.modifications[Config.SCENE_MODIFICATION_KEY].watermarkPosition =
            watermarkPosition;
    }
    if (scene.watermark) {
        const watermarkSourceData = getTemplateValueAttribute(scene.watermark, 'sourceData');
        if (isTemplateReference(watermarkSourceData)) {
            virtualSceneDesign.modifications[Config.SCENE_MODIFICATION_KEY].watermarkIndex =
                getTemplateIndex(watermarkSourceData);
        }
    }
    virtualSceneDesign.modifications[Config.SCENE_MODIFICATION_KEY].displayCaptions =
        scene.displayCaptions;
    return virtualSceneDesign;
};

const _isTextSizeUserOverridden = (
    textSize: number,
    placeholderTextSize: number,
    userOverrides: UserOverrides | null,
    sceneIds: string[],
): boolean => {
    /**
     * We automatically resize the text to be _smaller_ if necessary to fit on the scene, but not ever larger.
     * Because we only started tracking text size overrides in March 2025 we built in a workaround so anytime
     * the text in the scene is larger than the placeholder it's considered an override.
     */
    // check to see if the textSize is larger
    if (textSize > placeholderTextSize) {
        return true;
    }
    if (textSize < placeholderTextSize) {
        // see if it was overridden in any of the scenes
        for (const scene_id of sceneIds) {
            if (userOverrides && userOverrides[scene_id] && userOverrides[scene_id].text_size) {
                return true;
            }
        }
    }
    return false;
};

/**
 * Analyzes a video (in a luminary instance) and returns a list of VirtualSceneDesigns.
 * Each VirtualSceneDesign represents scenes that share the same modifications.
 */
export function getVirtualSceneDesigns({
    luminary,
    templateBundle,
    userOverrides,
}: {
    luminary: Luminary;
    templateBundle: TemplateBundle;
    userOverrides: UserOverrides;
}): VirtualSceneDesign[] {
    if (!luminary) throw new Error('luminary is required');
    if (!templateBundle) throw new Error('templateBundle is required');

    let virtualDesigns: VirtualSceneDesign[] = [];

    luminary.scenes.forEach((scene) => {
        // skip brand scenes
        if (scene.sceneCategory === Config.CATEGORY_BRAND) {
            return;
        }
        virtualDesigns.push(_getVirtualSceneDesignForScene(scene, userOverrides));
    });

    // for each unique scene design id get the config from the template (dedupe)
    const dataLuminary = new Luminary({
        fetchResources: false,
        disableRenderer: true,
        autoDraw: false,
        autoUpdate: false,
    });
    dataLuminary.aspectRatio = luminary.aspectRatio;
    dataLuminary.setTemplate(templateBundle);
    dataLuminary.template.load(luminary.template.save()); // load any template customizations
    const scene_design_ids = [...new Set(virtualDesigns.map((vDesign) => vDesign.baseDesign))];
    const templateVirtualDesigns: VirtualSceneDesign[] = [];
    for (const scene_design_id of scene_design_ids) {
        const scene = dataLuminary.addScene({ sceneDesignId: scene_design_id });
        templateVirtualDesigns.push(_getVirtualSceneDesignForScene(scene, userOverrides, true));
    }
    void dataLuminary.destroy();

    // update each unique_scene_config by removing any properties which match the templateVirtualDesigns
    virtualDesigns.forEach((virtualDesign) => {
        // get the matching template_scene_config (based on sceneDesign)
        const matchingTemplateVirtualDesign = templateVirtualDesigns.find((templateConfig) => {
            return templateConfig.baseDesign === virtualDesign.baseDesign;
        });
        if (!matchingTemplateVirtualDesign) {
            throw new Error(`No matching template found for virtualDesign: ${virtualDesign}`);
        }
        // remove any matching properties for assets
        Object.keys(matchingTemplateVirtualDesign.modifications).forEach((assetName) => {
            if (!virtualDesign.modifications[assetName]) {
                return;
            }
            Object.keys(matchingTemplateVirtualDesign.modifications[assetName]).forEach(
                (property) => {
                    // special comparison logic for textSize property
                    if (property === 'textSize') {
                        if (
                            !_isTextSizeUserOverridden(
                                virtualDesign.modifications[assetName][property],
                                matchingTemplateVirtualDesign.modifications[assetName][property],
                                userOverrides,
                                virtualDesign.sceneIds,
                            )
                        ) {
                            delete virtualDesign.modifications[assetName][property];
                        }
                        return;
                    }

                    // special comparison for colorGraphicsMap property
                    if (property === 'colorGraphicsMap') {
                        const virtualDesignColorGraphicsMap =
                            virtualDesign.modifications[assetName][property];
                        const matchingTemplateColorGraphicsMap =
                            matchingTemplateVirtualDesign.modifications[assetName][property];
                        if (
                            isEqual(virtualDesignColorGraphicsMap, matchingTemplateColorGraphicsMap)
                        ) {
                            delete virtualDesign.modifications[assetName][property];
                        }
                        // delete if empty
                        if (Object.keys(virtualDesignColorGraphicsMap).length === 0) {
                            delete virtualDesign.modifications[assetName][property];
                        }
                        return;
                    }

                    if (
                        isEqual(
                            matchingTemplateVirtualDesign.modifications[assetName][property],
                            virtualDesign.modifications[assetName][property],
                        )
                    ) {
                        delete virtualDesign.modifications[assetName][property];
                    }
                },
            );
        });
        // remove any empty objects
        Object.keys(virtualDesign.modifications).forEach((assetName) => {
            if (Object.keys(virtualDesign.modifications[assetName]).length === 0) {
                delete virtualDesign.modifications[assetName];
            }
        });
    });

    // remove any virtual scene designs which now have no modifications
    virtualDesigns = virtualDesigns.filter(
        (config) => Object.keys(config.modifications).length > 0,
    );

    // merge any virtual scene designs which have the same baseDesign and same modifications
    const dedupedVirtualDesigns: VirtualSceneDesign[] = [];
    virtualDesigns.forEach((config) => {
        const matchingConfig = dedupedVirtualDesigns.find((dedupedConfig) => {
            return (
                dedupedConfig.baseDesign === config.baseDesign &&
                JSON.stringify(dedupedConfig.modifications) === JSON.stringify(config.modifications)
            );
        });
        if (matchingConfig) {
            matchingConfig.sceneIds.push(...config.sceneIds);
        } else {
            dedupedVirtualDesigns.push(config);
        }
    });

    return dedupedVirtualDesigns;
}
